import React, { useEffect, useRef, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  TextField,
} from "@material-ui/core";
import { Alert } from "@material-ui/lab";
import { makeStyles } from "@material-ui/core/styles";
import AddIcon from "@material-ui/icons/Add";
import CloseIcon from "@material-ui/icons/Close";
import EditOutlinedIcon from "@material-ui/icons/EditOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";

import ProcessusService from "../../../services/smq/ProcessusService";
import ButtonYellow from "../component/BouttonYellow";

const DEFAULT_VALUES = {
  id: "",
  name: "",
  category: "",
};


const getAuthenticatedUserId = () => {
  const storedUser = localStorage.getItem("USER");
  if (!storedUser) return null;

  try {
    const parsedUser = JSON.parse(storedUser);
    if (parsedUser && typeof parsedUser === "object") {
      return parsedUser.user_id ?? parsedUser.id ?? null;
    }
    return parsedUser;
  } catch (error) {
    return storedUser;
  }
};

const useStyles = makeStyles((theme) => ({
  paper: {
    borderRadius: 18,
    maxWidth: 820,
    overflow: "hidden",
  },
  titleRoot: {
    alignItems: "center",
    borderBottom: "1px solid #d5dee9",
    display: "flex",
    justifyContent: "space-between",
    padding: "13px 20px",
  },
  titleGroup: {
    alignItems: "center",
    display: "flex",
    gap: 7,
    minWidth: 0,
  },
  titleIcon: {
    color: "#6962a7",
    flexShrink: 0,
    fontSize: 20,
  },
  title: {
    color: "#10263d",
    fontFamily: "Georgia, 'Times New Roman', serif",
    fontSize: 18,
    fontWeight: 700,
    lineHeight: 1.25,
    margin: 0,
  },
  closeButton: {
    borderColor: "#e8b8b8",
    borderRadius: 6,
    color: "#b3261e",
    flexShrink: 0,
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12,
    fontWeight: 700,
    minWidth: 87,
    padding: "5px 10px",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#fff5f5",
      borderColor: "#d99494",
    },
  },
  content: {
    padding: "18px 20px 29px",
    "&:first-child": {
      paddingTop: 18,
    },
  },
  categoryAlert: {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 11,
    marginBottom: 10,
  },
  retryButton: {
    color: "inherit",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 10,
    fontWeight: 700,
    textTransform: "none",
  },
  loadingOption: {
    alignItems: "center",
    display: "flex",
    gap: 8,
  },
  fieldGroup: {
    marginBottom: 10,
    "&:last-child": {
      marginBottom: 0,
    },
  },
  fieldLabel: {
    color: "#486786",
    display: "block",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.04em",
    marginBottom: 4,
    textTransform: "uppercase",
  },
  field: {
    "& .MuiOutlinedInput-root": {
      backgroundColor: "#fff",
      borderRadius: 6,
      color: "#17324d",
      fontFamily: "'DM Sans', sans-serif",
      fontSize: 12,
      minHeight: 30,
      "& fieldset": {
        borderColor: "#c9d6e5",
      },
      "&:hover fieldset": {
        borderColor: "#96adc4",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#587b9d",
        borderWidth: 1,
      },
      "&.Mui-disabled": {
        backgroundColor: "#f1f4f7",
      },
    },
    "& .MuiOutlinedInput-input": {
      padding: "7px 10px",
    },
    "& .MuiSelect-outlined": {
      paddingRight: 32,
    },
    "& .MuiFormHelperText-root": {
      fontFamily: "'DM Sans', sans-serif",
      fontSize: 10,
      margin: "3px 0 0",
    },
  },
  actions: {
    borderTop: "1px solid #d5dee9",
    gap: 10,
    justifyContent: "flex-end",
    padding: "14px 20px",
    [theme.breakpoints.down("xs")]: {
      alignItems: "stretch",
      flexDirection: "column-reverse",
      "& > :not(:first-child)": {
        marginLeft: 0,
      },
    },
  },
  cancelButton: {
    color: "#071a2c",
    fontFamily: "'DM Sans', sans-serif",
    fontSize: 12,
    fontWeight: 700,
    minWidth: 70,
    textTransform: "none",
  },
}));

const ReferentielAdd = ({
  categories = [],
  categoriesError = null,
  categoriesLoading = false,
  existingIds = [],
  initialValues = DEFAULT_VALUES,
  mode = "create",
  onClose,
  onRefreshCategories,
  onSaved,
  open,
}) => {
  const classes = useStyles();
  const idInputRef = useRef(null);
  const nameInputRef = useRef(null);
  const categoryInputRef = useRef(null);
  const [form, setForm] = useState(DEFAULT_VALUES);
  const [errors, setErrors] = useState({});
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);

  const isEdit = mode === "edit";
  const initialId = initialValues?.id || "";
  const initialName = initialValues?.name || "";
  const initialCategory = initialValues?.category ?? "";
  const referentielId = initialValues?.referentielId;
  const defaultCategory = categories[0]?.value ?? "";

  useEffect(() => {
    if (!open) return;

    setForm({
      id: initialId.toUpperCase(),
      name: initialName,
      category: initialCategory,
    });
    setErrors({});
    setSaveError(null);
    setSaving(false);
  }, [
    initialCategory,
    initialId,
    initialName,
    open,
  ]);

  useEffect(() => {
    if (!open || defaultCategory === "") return;

    setForm((current) =>
      current.category === ""
        ? { ...current, category: defaultCategory }
        : current
    );
    setErrors((current) =>
      current.category ? { ...current, category: undefined } : current
    );
  }, [defaultCategory, open]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({
      ...current,
      [name]: name === "id" ? value.toUpperCase() : value,
    }));
    setErrors((current) => ({ ...current, [name]: undefined }));
    setSaveError(null);
  };

  const validate = () => {
    const nextErrors = {};
    const normalizedId = form.id.trim().toUpperCase();
    const normalizedName = form.name.trim();
    const normalizedInitialId = initialId.trim().toUpperCase();
    const normalizedExistingIds = existingIds.map((id) =>
      String(id).trim().toUpperCase()
    );

    if (!normalizedId) {
      nextErrors.id = "L’ID unique est obligatoire.";
    }  else if (
      normalizedId !== normalizedInitialId &&
      normalizedExistingIds.includes(normalizedId)
    ) {
      nextErrors.id = "Cet ID existe déjà dans le référentiel.";
    }

    if (!normalizedName) {
      nextErrors.name = "Le nom complet est obligatoire.";
    }

    if (!form.category) {
      nextErrors.category = "La catégorie est obligatoire.";
    } else if (
      !categories.some((category) => category.value === form.category)
    ) {
      nextErrors.category = "La catégorie sélectionnée n’est pas valide.";
    }

    setForm((current) => ({
      ...current,
      id: normalizedId,
      name: normalizedName,
    }));
    setErrors(nextErrors);

    if (nextErrors.id) {
      idInputRef.current?.focus();
    } else if (nextErrors.name) {
      nameInputRef.current?.focus();
    } else if (nextErrors.category) {
      categoryInputRef.current?.focus();
    }

    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving || !validate()) return;

    const payload = {
      categorie_id: form.category,
      referentiel_code: form.id.trim().toUpperCase(),
      referentiel_nom: form.name.trim(),
    };

    if (!isEdit) {
      const userId = getAuthenticatedUserId();
      if (!userId) {
        setSaveError(
          "Impossible d’identifier l’utilisateur connecté. Veuillez vous reconnecter."
        );
        return;
      }
      payload.user_id = userId;
    }

    if (isEdit && !referentielId) {
      setSaveError("Le référentiel à modifier est introuvable.");
      return;
    }

    setSaving(true);
    setSaveError(null);
    try {
      if (isEdit) {
        await ProcessusService.updateReferentiel(referentielId, payload);
      } else {
        await ProcessusService.createReferentiel(payload);
      }
      if (onSaved) await onSaved();
      if (onClose) onClose();
    } catch (error) {
      setSaveError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleRefreshCategories = () => {
    if (!onRefreshCategories) return;
    Promise.resolve(onRefreshCategories()).catch(() => undefined);
  };

  const handleClose = (...args) => {
    if (!saving && onClose) onClose(...args);
  };

  const dialogTitle = isEdit
    ? "Modifier le processus (référentiel)"
    : "Nouveau référentiel processus";

  return (
    <Dialog
      aria-describedby="referentiel-dialog-description"
      aria-labelledby="referentiel-dialog-title"
      classes={{ paper: classes.paper }}
      fullWidth
      maxWidth="md"
      onClose={handleClose}
      open={open}
    >
      <form noValidate onSubmit={handleSubmit}>
        <DialogTitle className={classes.titleRoot} disableTypography>
          <div className={classes.titleGroup}>
            {isEdit ? (
              <EditOutlinedIcon className={classes.titleIcon} />
            ) : (
              <AddIcon className={classes.titleIcon} />
            )}
            <h2 className={classes.title} id="referentiel-dialog-title">
              {dialogTitle}
            </h2>
          </div>
          <Button
            className={classes.closeButton}
            disabled={saving}
            onClick={handleClose}
            startIcon={<CloseIcon fontSize="small" />}
            type="button"
            variant="outlined"
          >
            Fermer
          </Button>
        </DialogTitle>

        <DialogContent className={classes.content}>
          {saveError && (
            <Alert className={classes.categoryAlert} severity="error">
              {saveError}
            </Alert>
          )}

          <div className={classes.fieldGroup}>
            <label className={classes.fieldLabel} htmlFor="referentiel-id">
              ID unique *
            </label>
            <TextField
              className={classes.field}
              autoFocus={!isEdit}
              disabled={isEdit || saving}
              error={Boolean(errors.id)}
              fullWidth
              helperText={errors.id}
              id="referentiel-id"
              inputProps={{ maxLength: 50 }}
              inputRef={idInputRef}
              name="id"
              onChange={handleChange}
              placeholder="PROC_XXX"
              size="small"
              value={form.id}
              variant="outlined"
            />
          </div>

          <div className={classes.fieldGroup}>
            <label className={classes.fieldLabel} htmlFor="referentiel-name">
              Nom complet *
            </label>
            <TextField
              autoFocus={isEdit}
              className={classes.field}
              disabled={saving}
              error={Boolean(errors.name)}
              fullWidth
              helperText={errors.name}
              id="referentiel-name"
              inputProps={{ maxLength: 250 }}
              inputRef={nameInputRef}
              name="name"
              onChange={handleChange}
              placeholder="Libellé complet du processus"
              size="small"
              value={form.name}
              variant="outlined"
            />
          </div>

          <div className={classes.fieldGroup}>
            {categoriesError && (
              <Alert
                action={
                  <Button
                    className={classes.retryButton}
                    disabled={categoriesLoading || saving}
                    onClick={handleRefreshCategories}
                    startIcon={<RefreshIcon fontSize="small" />}
                    type="button"
                  >
                    Réessayer
                  </Button>
                }
                className={classes.categoryAlert}
                severity="error"
              >
                Il est impossible de charger les catégories de processus. Veuillez réessayer.
              </Alert>
            )}
            <label className={classes.fieldLabel} htmlFor="referentiel-category">
              Catégorie *
            </label>
            <TextField
              className={classes.field}
              disabled={
                saving || categoriesLoading || categories.length === 0
              }
              error={Boolean(errors.category)}
              fullWidth
              helperText={
                errors.category ||
                (categoriesLoading
                  ? "Chargement des catégories…"
                  : !categoriesError && categories.length === 0
                  ? "Aucune catégorie disponible."
                  : undefined)
              }
              id="referentiel-category"
              inputRef={categoryInputRef}
              name="category"
              onChange={handleChange}
              select
              size="small"
              value={form.category}
              variant="outlined"
            >
              {categoriesLoading && (
                <MenuItem disabled value="">
                  <span className={classes.loadingOption}>
                    <CircularProgress size={14} />
                    Chargement des catégories…
                  </span>
                </MenuItem>
              )}
              {categories.map((category) => (
                <MenuItem key={category.value} value={category.value}>
                  {category.label}
                </MenuItem>
              ))}
            </TextField>
          </div>
        </DialogContent>

        <DialogActions className={classes.actions}>
          <Button
            className={classes.cancelButton}
            disabled={saving}
            onClick={handleClose}
            type="button"
          >
            Annuler
          </Button>
          <ButtonYellow disabled={saving} type="submit">
            {saving ? (
              <CircularProgress color="inherit" size={20} />
            ) : isEdit ? (
              "Enregistrer les modifications"
            ) : (
              "Créer dans le référentiel"
            )}
          </ButtonYellow>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ReferentielAdd;
